import { SpriteSheetPlayer } from '@/components/ui/sprite-sheet-player';
import appLogoSheet from '@/assets/spritesheets/app-logo-anim.png';
import appLogoData from '@/assets/spritesheets/app-logo-anim.json';
import staticLogo from '@/assets/logo.svg';
import { cn } from '@/lib/utils';
import type { AppLogoData } from './types';

const typedAppLogoData = appLogoData as AppLogoData;

const sortedFrames = Object.entries(typedAppLogoData.frames).sort(([a], [b]) => {
  const indexA = Number.parseInt(a.replace(/\D/g, ''), 10);
  const indexB = Number.parseInt(b.replace(/\D/g, ''), 10);
  return indexA - indexB;
});

const appLogoFrames = sortedFrames.map(([, { frame }]) => ({
  x: frame.x,
  y: frame.y,
  w: frame.w,
  h: frame.h,
}));

const logicalSize = sortedFrames[0]?.[1].sourceSize ?? { w: 500, h: 78 };

export type AppLogoProps = {
  className?: string;
  /** Rendered width in CSS pixels; height is derived from the logical frame aspect ratio. */
  width?: number;
  /** When true, renders the static SVG logo instead of the animated spritesheet. */
  disableAnimation?: boolean;
};

/**
 * Brand wordmark.
 *
 * By default renders the `app-logo-anim` spritesheet in endless ping-pong with a 5-second
 * pause between iterations. Pass `disableAnimation` to fall back to the static SVG.
 */
export function AppLogo({ className, width = 190, disableAnimation = false }: AppLogoProps) {
  const height = (width * logicalSize.h) / logicalSize.w;

  if (disableAnimation) {
    return <img src={staticLogo} alt="App Logo" width={width} height={height} className={cn(className)} />;
  }

  return (
    <SpriteSheetPlayer
      spriteSheetSrc={appLogoSheet}
      sheetWidth={typedAppLogoData.meta.size.w}
      sheetHeight={typedAppLogoData.meta.size.h}
      frames={appLogoFrames}
      frameDurationMs={30}
      playback="pingpong"
      loopDelayMs={5000}
      width={width}
      height={height}
      className={cn(className)}
    />
  );
}
